import {
  LocationInterface,
  LocationScope,
  LocationVarInheritance,
  LocationVarInterface,
  LocationVarKey,
  LocationVarsWithIdByKey,
  LocationVarsWithIdByKeyByGroupOrLocationId,
  LocationWithVariablesInterface,
  MergedLocationVar,
  UnitInterface,
  VarDef,
  VarKey,
  VarType,
} from "@swivl-mono/types";

import { VarsHelper } from "./Vars.Helper";

export function GetLocationsWithVariables(
  locations: LocationInterface[],
  orgVariables?: LocationVarsWithIdByKey,
  groupVariables?: LocationVarsWithIdByKeyByGroupOrLocationId,
  locationVariables?: LocationVarsWithIdByKeyByGroupOrLocationId,
  units?: { [locationId: number]: UnitInterface[] },
): { [locationId: number]: LocationWithVariablesInterface<any> } | undefined {
  if (!locations || locations.length === 0 || !orgVariables || !groupVariables || !locationVariables) {
    return undefined;
  }

  const locationsWithVariables: { [locationId: number]: LocationWithVariablesInterface<any> } = {};
  for (let location of locations) {
    locationsWithVariables[location.id] = {
      location: location,
      variables: Object.values(LocationVarKey).reduce((acc, key) => {
        const val = GetLocationVarFromVariables(true, orgVariables, groupVariables, locationVariables, key, location.groupId, location.id);
        if (val && val.value && val.value.length > 0) {
          acc[key] = val;
        }
        return acc;
      }, {} as any),
    };
  }
  return locationsWithVariables as any;
}

export function GetLocationVarFromVariables(
  isLoaded: boolean,
  orgVariables: LocationVarsWithIdByKey,
  groupVariables: LocationVarsWithIdByKeyByGroupOrLocationId,
  locationVariables: LocationVarsWithIdByKeyByGroupOrLocationId,

  key: LocationVarKey,
  groupId: number | undefined,
  locationId: number | undefined,
): MergedLocationVar | undefined {
  if (!isLoaded) {
    return undefined;
  }
  const varKey = VarsHelper.varKeyForType(VarType.Location, key);

  if (locationId) {
    if (locationVariables?.[locationId]?.[key]) {
      return {
        ...locationVariables?.[locationId]?.[key]!,
        inheritance: LocationVarInheritance.NONE,
      };
    }

    if (groupId && groupVariables?.[groupId]?.[key]) {
      return {
        ...groupVariables?.[groupId]?.[key]!,
        inheritance: LocationVarInheritance.GROUP,
      };
    }

    if (orgVariables?.[key]) {
      return {
        ...orgVariables?.[key]!,
        inheritance: LocationVarInheritance.ORG,
      };
    }
    if (varKey && VarDef[varKey] && typeof VarDef[varKey]?.defaultValue === "string") {
      return {
        key,
        value: VarDef[varKey]!.defaultValue,
        inheritance: LocationVarInheritance.DEFAULT,
      };
    }
    return;
  }

  if (groupId) {
    if (groupVariables?.[groupId]?.[key]) {
      return {
        ...groupVariables?.[groupId]?.[key]!,
        inheritance: LocationVarInheritance.NONE,
      };
    }
    if (orgVariables?.[key]) {
      return {
        ...orgVariables?.[key]!,
        inheritance: LocationVarInheritance.ORG,
      };
    }
    if (varKey && VarDef[varKey] && typeof VarDef[varKey]?.defaultValue === "string") {
      return {
        key,
        value: VarDef[varKey]!.defaultValue,
        inheritance: LocationVarInheritance.DEFAULT,
      };
    }
    return;
  }

  if (orgVariables?.[key]) {
    return {
      ...orgVariables?.[key]!,
      inheritance: LocationVarInheritance.NONE,
    };
  }

  if (varKey && VarDef[varKey] && typeof VarDef[varKey]?.defaultValue === "string") {
    return {
      key,
      value: VarDef[varKey]!.defaultValue,
      inheritance: LocationVarInheritance.DEFAULT,
    };
  }

  return undefined;
}

export function LocationsWithVariables<T extends LocationVarKey>(
  orgId: number,
  locations: LocationInterface[],
  keys: T[],
  variables: LocationVarInterface[],
): LocationWithVariablesInterface<T>[] {
  let output: LocationWithVariablesInterface<T>[] = [];

  for (let i = 0; i < locations.length; i++) {
    let vars: any = {};
    keys.forEach((key) => {
      if (key) {
        const varKey: VarKey = ("Location:" + key) as VarKey;
        vars[key] = VarDef[varKey] && VarDef[varKey].defaultValue ? VarDef[varKey].defaultValue : "";
      }
    });
    let outputRow: LocationWithVariablesInterface<T> = { location: locations[i], variables: vars };
    let setFrom: { [key: string]: LocationScope } = {};
    for (let j = 0; j < variables.length; j++) {
      const variable = variables[j];
      const key = variable.key as T;

      if (variables[j].locationId === locations[i].id) {
        outputRow.variables[key] = variable.value;
        setFrom[key] = LocationScope.LOCATION;
      } else if (variables[j].groupId === locations[i].groupId && setFrom[key] !== LocationScope.LOCATION) {
        outputRow.variables[key] = variable.value;
        setFrom[key] = LocationScope.GROUP;
      } else if (!variable.groupId && !variable.locationId && setFrom[key] !== LocationScope.LOCATION && setFrom[key] !== LocationScope.GROUP) {
        outputRow.variables[key] = variable.value;
        setFrom[key] = LocationScope.ORG;
      }
    }

    output.push(outputRow);
  }

  return output;
}
